import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useContentStore } from '../../context/ContentContext';

const tabs = [
    { label: 'Dashboard', path: '/admin/dashboard' },
    { label: 'Portfolio', path: '/admin/portfolio' },
    { label: 'Page Content', path: '/admin/content' }
];

/**
 * Shared chrome for every admin screen: auth guard, header, section tabs.
 */
const AdminShell = ({ title, subtitle, viewLink = '/', children }) => {
    const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
    const { storageMode } = useContentStore();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/admin');
        }
    }, [isAuthenticated, authLoading, navigate]);

    const handleLogout = () => {
        logout();
        navigate('/admin');
    };

    if (authLoading || !isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-40">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4">
                            <img src="/images/logo.png" alt="Knitster LDN" className="h-10" />
                            <div>
                                <h1 className="font-poppins font-semibold text-white text-lg">{title}</h1>
                                <p className="text-gray-400 text-sm">
                                    {subtitle || 'Admin Dashboard'}
                                    <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                        storageMode === 'api'
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                        {storageMode === 'api' ? '● Production' : '● Prototype'}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <a
                                href={viewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 text-gray-300 hover:text-white transition-colors text-sm"
                            >
                                View Live Site →
                            </a>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                            >
                                Logout
                            </button>
                        </div>
                    </div>

                    {/* Section tabs */}
                    <nav className="flex gap-1 mt-4 -mb-4">
                        {tabs.map(tab => (
                            <Link
                                key={tab.path}
                                to={tab.path}
                                className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                                    location.pathname === tab.path
                                        ? 'border-primary-red text-white bg-gray-900'
                                        : 'border-transparent text-gray-400 hover:text-white'
                                }`}
                            >
                                {tab.label}
                            </Link>
                        ))}
                    </nav>
                </div>
            </header>

            {children}
        </div>
    );
};

export default AdminShell;
